import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from "react";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import styles from "./Billing.module.css";
import PaymeFormContainer from "../../../../components/PaymeForm/PaymeFormContainer";
import { PaymeFormRef } from "../../../../components/PaymeForm/PaymeForm";

// Add customerData types
interface CustomerData {
  firstName: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  phone: string;
}

interface BillingProps {
  onConfirm: () => void;
  onChange: (data: any) => void;
  onEdit: () => void;
  onOrderSubmit: (billingData?: any) => Promise<void>; // Final order submission callback
  amount: number;
  customerData: CustomerData;
  shippingData: {
    email: string;
    address: string;
    district: string;
    province: string;
    department: string;
    country: string;
    ubigeoCode: string;
  };
  onPaymentProcessing?: () => void; // Callback for when payment processing starts
  onPaymentError?: (error: Error) => void; // NEW: Callback for payment errors
}

// Update FormData interface to include paymeResponse
interface FormData {
  paymentMethod: string;
  cardNumber: string;
  expDate: string;
  cvv: string;
  paymeResponse?: {
    id: string;
    card?: {
      last4: string;
    };
  };
}

// Expose Billing methods via ref
export interface BillingRef {
  processPayment: () => Promise<void>;
}

const Billing = forwardRef<BillingRef, BillingProps>((props, ref) => {
  const {
    onConfirm,
    onChange,
    onEdit,
    onOrderSubmit,
    amount,
    customerData,
    shippingData,
    onPaymentProcessing, // Passed from Checkout
    onPaymentError, // NEW: Passed from Checkout
  } = props;

  // Log the amount received by Billing
  useEffect(() => {
    console.log("[Billing] Received amount prop:", amount);
  }, [amount]);

  const paymeFormRef = useRef<PaymeFormRef>(null);

  // Expose processPayment from the PaymeForm via ref
  useImperativeHandle(ref, () => ({
    processPayment: async () => {
      if (paymeFormRef.current) {
        await paymeFormRef.current.processPayment();
      }
    },
  }));

  const [formData] = useState<FormData>({
    paymentMethod: "",
    cardNumber: "",
    expDate: "",
    cvv: "",
  });
  const [tempPaymentMethod, setTempPaymentMethod] = useState<string>("");
  const [formErrors, setFormErrors] = useState<{
    paymentMethod?: string;
    cardNumber: string;
    name: string;
    expDate: string;
    cvv: string;
    general?: string;
    paymentError?: string; // Add specific field for payment errors
  }>({
    paymentMethod: "",
    cardNumber: "",
    name: "",
    expDate: "",
    cvv: "",
  });
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [paymeFormVisible, setPaymeFormVisible] = useState(false);
  const [paymeFormMounted, setPaymeFormMounted] = useState(false);
  const [paymeFormKey, setPaymeFormKey] = useState(Date.now());

  // Use ref for tracking component mount state to prevent memory leaks
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handlePaymentMethodChange = (method: string) => {
    console.log(`Billing: Payment method changed to ${method}`);

    // Clear form errors
    setFormErrors((prev) => ({
      ...prev,
      paymentError: undefined,
      general: undefined,
    }));

    // Set the payment method
    setTempPaymentMethod(method);

    // Handle PayMe form visibility directly
    if (method === "payme") {
      console.log("Billing: Showing PayMe form");
      // Generate a new key for a fresh PaymeForm instance if needed
      setPaymeFormKey(Date.now());
      setPaymeFormVisible(true);
      setPaymeFormMounted(true);
    } else {
      console.log("Billing: Hiding PayMe form");
      setPaymeFormVisible(false);
      setPaymeFormMounted(false);
    }
  };

  // Handle confirm for non-Payme methods
  const handleConfirm = async () => {
    if (isSubmittingOrder) return;

    // Make sure we have a valid payment method
    if (!tempPaymentMethod) {
      setFormErrors({
        ...formErrors,
        general: "Debe seleccionar un método de pago",
        paymentMethod: "Debe seleccionar un método de pago",
      });
      return;
    }

    console.log(`Billing: Selected payment method - ${tempPaymentMethod}`);
    const updatedBillingData = { paymentMethod: tempPaymentMethod };

    if (
      tempPaymentMethod === "yapePlin" ||
      tempPaymentMethod === "money_transfer"
    ) {
      console.log(
        `Billing: Directly generating order for ${tempPaymentMethod}...`
      );
      setIsSubmittingOrder(true);
      try {
        await onOrderSubmit(updatedBillingData);
        console.log(
          `Billing: Order submission for ${tempPaymentMethod} completed.`
        );
      } catch (error) {
        console.error(
          `Billing: Error generating order for ${tempPaymentMethod}`,
          error
        );
      } finally {
        setIsSubmittingOrder(false);
      }
      return;
    }

    // Otherwise, for Payme, store billing data and signal confirmation
    onChange(updatedBillingData);
    onConfirm();
  };

  const handleEdit = () => {
    setIsConfirmed(false);
    onEdit();
  };

  const handlePaymeSuccess = async (response: any) => {
    console.log("Billing: Payme success response:", response);

    // Check if the status code indicates success (code "00")
    if (response.transaction?.meta?.status?.code === "00") {
      const paymeBillingData = {
        paymentMethod: "payme",
        paymeResponse: response,
      };
      try {
        await onOrderSubmit(paymeBillingData);
      } catch (error) {
        console.error("Billing: Error generating order for Payme", error);
      }
    } else {
      // This shouldn't happen normally as errors should be caught in PaymeForm
      console.error("Billing: Received non-success response:", response);
      handlePaymeError(new Error("Error en el procesamiento del pago"));
    }
  };

  const handlePaymeError = (error: Error) => {
    console.log("Billing: Payment error received:", error.message);

    // Make sure to hide the overlay when there's an error
    setIsSubmittingOrder(false);

    // Update error state
    setFormErrors({
      ...formErrors,
      general: "",
      paymentError: error.message || "Error processing card. Please try again.",
    });

    // Call the parent error handler to hide overlay
    if (onPaymentError) {
      onPaymentError(error);
    }

    // To completely reload the form from scratch:
    // 1. Unmount the form
    setPaymeFormMounted(false);

    // 2. Generate a new key to force a complete remount
    setPaymeFormKey(Date.now());

    // 3. Wait a bit and then remount the form
    setTimeout(() => {
      if (isMounted.current) {
        console.log("Billing: Remounting form after error");
        setPaymeFormMounted(true);
      }
    }, 500);
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardContent}>
        {isSubmittingOrder && (
          <div className={styles.orderOverlay}>
            <div>Procesando orden...</div>
          </div>
        )}
        <div className={styles.header}>
          <h2 className={styles.cardTitle}>Paso 3 / 3: Método de Pago</h2>
          {isConfirmed && (
            <CheckCircleOutlineIcon className={styles.checkIcon} />
          )}
        </div>
        <div className={styles.content}>
          {!isConfirmed ? (
            <>
              <div className={styles.toggleButtonGroup}>
                <button
                  className={`${styles.toggleButton} ${
                    tempPaymentMethod === "yapePlin" ? styles.active : ""
                  }`}
                  onClick={() => handlePaymentMethodChange("yapePlin")}
                >
                  <PhoneIphoneIcon />
                  <span>Yape/Plin</span>
                </button>
                <button
                  className={`${styles.toggleButton} ${
                    tempPaymentMethod === "money_transfer" ? styles.active : ""
                  }`}
                  onClick={() => handlePaymentMethodChange("money_transfer")}
                >
                  <AccountBalanceIcon />
                  <span>Transf. Bancaria</span>
                </button>
                <button
                  className={`${styles.toggleButton} ${
                    tempPaymentMethod === "payme" ? styles.active : ""
                  }`}
                  onClick={() => handlePaymentMethodChange("payme")}
                >
                  <CreditCardIcon />
                  <span>T. Crédito / Débito</span>
                </button>
              </div>

              {tempPaymentMethod === "yapePlin" && (
                <div className={styles.paymentMessage}>
                  <div className={styles.paymentIconContainer}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_FOLDER}/payment_method_yape_1018da3083.svg`}
                      alt="Yape QR"
                      className={styles.paymentIconImage}
                    />
                    <p>
                      <strong>¡Paga con Yape o Plin!</strong>
                      <br />
                      Paga de manera super fácil y segura, ¡solo necesitas un
                      dispositivo que tenga Yape, Plin o de la misma app del
                      BCP!
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className={styles.confirmButton}
                    disabled={isSubmittingOrder}
                  >
                    Confirmar la orden
                  </button>
                </div>
              )}
              {tempPaymentMethod === "money_transfer" && (
                <div className={styles.paymentMessage}>
                  <h3>BCP - Cuenta de Ahorros</h3>
                  <p>
                    <strong>Titular:</strong> Evefant SAC
                  </p>
                  <p>
                    <strong>Número de Cuenta:</strong>
                    <br />
                    194-9074-6492-0-20{" "}
                    <ContentCopyIcon
                      className={styles.copyIcon}
                      onClick={() =>
                        navigator.clipboard.writeText("19490746492020")
                      }
                    />
                  </p>
                  <p>
                    <strong>CCI:</strong>
                    <br />
                    002-194-19074-6492-0-2091{" "}
                    <ContentCopyIcon
                      className={styles.copyIcon}
                      onClick={() =>
                        navigator.clipboard.writeText("00219419074649202091")
                      }
                    />
                  </p>
                  <p>
                    <strong>Instrucciones:</strong>
                    <br />
                    Una vez hayas confirmado tu orden, esta se reservará por 3
                    horas. Una vez realizada la transferencia, deberás enviar
                    una captura de pantalla por Whatsapp o correo.
                  </p>
                  <p>
                    <strong>Whatsapp:</strong> 906 869 576{" "}
                    <ContentCopyIcon
                      className={styles.copyIcon}
                      onClick={() => navigator.clipboard.writeText("906869576")}
                    />
                  </p>
                  <p>
                    <strong>Correo:</strong> ventas@evefant.com{" "}
                    <ContentCopyIcon
                      className={styles.copyIcon}
                      onClick={() =>
                        navigator.clipboard.writeText("ventas@evefant.com")
                      }
                    />
                  </p>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className={styles.confirmButton}
                    disabled={isSubmittingOrder}
                  >
                    Confirmar la orden
                  </button>
                </div>
              )}
              {tempPaymentMethod === "payme" && (
                <div className={`${styles.paymeForm} ${styles.active}`}>
                  <div
                    className={styles.paymeFormWrapper}
                    data-testid="payme-form-wrapper"
                  >
                    {formErrors.paymentError && (
                      <div className={styles.paymentErrorBox}>
                        <p>{formErrors.paymentError}</p>
                      </div>
                    )}

                    {/* Always render the form container, but conditionally show loading state */}
                    <div className={styles.formBuffer}>
                      {!paymeFormMounted ? (
                        <div className={styles.loadingFormPlaceholder}>
                          Cargando formulario de pago...
                        </div>
                      ) : (
                        <>
                          {console.log(
                            "[Billing] Passing amount to PaymeFormContainer:",
                            amount
                          )}
                          <PaymeFormContainer
                            // Use key to force remount when needed
                            key={paymeFormKey}
                            ref={paymeFormRef}
                            customerData={{
                              ...customerData,
                              email: shippingData.email,
                            }}
                            shippingData={shippingData}
                            amount={amount}
                            onSuccess={(response: any) => {
                              console.log("PaymeForm success:", response);
                              if (
                                response.transaction?.meta?.status?.code ===
                                "00"
                              ) {
                                handlePaymeSuccess(response);
                              } else {
                                handlePaymeError(
                                  new Error(
                                    response.transaction?.meta?.status
                                      ?.message_ilgn?.[0]?.value ||
                                      "Error procesando el pago"
                                  )
                                );
                              }
                            }}
                            onError={handlePaymeError}
                            onStartProcessing={onPaymentProcessing}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {formErrors.general && (
                <div className={styles.errorMessage}>{formErrors.general}</div>
              )}
            </>
          ) : (
            <div className={styles.confirmationMessage}>
              <p>
                Método de Pago:{" "}
                <strong>
                  {tempPaymentMethod === "yapePlin"
                    ? "Yape / Plin"
                    : tempPaymentMethod === "money_transfer"
                    ? "Transferencia Bancaria"
                    : "Tarjeta de Crédito/Débito"}
                </strong>
              </p>
              {tempPaymentMethod === "yapePlin" && (
                <p>
                  Instrucciones: <br />
                  <strong>
                    ¡Recibirás los detalles para yapearnos al Confirmar tu
                    Orden!
                  </strong>
                </p>
              )}
              {tempPaymentMethod === "money_transfer" && (
                <p>
                  Instrucciones: <br />
                  <strong>
                    ¡Recibirás los detalles de transferencia al Confirmar tu
                    Orden!
                  </strong>
                </p>
              )}
              {tempPaymentMethod === "payme" && formData.paymeResponse && (
                <div className={styles.paymentDetails}>
                  <p>
                    Payment Status: <strong>Authorized</strong>
                  </p>
                  <p>
                    Transaction ID: <strong>{formData.paymeResponse.id}</strong>
                  </p>
                  <p>
                    Card:{" "}
                    <strong>
                      **** **** **** {formData.paymeResponse.card?.last4}
                    </strong>
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={handleEdit}
                className={styles.editButton}
              >
                Editar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

Billing.displayName = "Billing";

export default Billing;
